<template>
    <div @click="showNewFileDialog = true">
        <Button
            icon="pi pi-plus"
            :label="label"
            :size="size"
            :severity="severity"
            :text="textButton"
        />

        <!-- New file popup -->
        <Dialog
            :style="{ minWidth: '350px' }"
            v-model:visible="showNewFileDialog"
            :modal="true"
            :autoZIndex="true"
            @hide="newFileType = ''"
            :header="header"
        >
            <Spinner v-if="isUploading" />
            <div v-if="!newFileType" class="grid p-fluid">
                <div class="col-12">
                    <h3>Select type</h3>
                    <SelectButton
                        v-for="i in Math.ceil(
                            filteredFileTypes.length / elementsInRow
                        )"
                        :key="i"
                        v-model="newFileType"
                        :options="
                            filteredFileTypes.slice(
                                (i - 1) * elementsInRow,
                                i * elementsInRow
                            )
                        "
                        optionLabel="name"
                        optionValue="value"
                    />
                </div>
            </div>
            <form
                v-if="
                    [
                        'markdown',
                        'dmn',
                        'bpmn',
                        'form',
                        'domain',
                        'feature',
                    ].includes(newFileType)
                "
                class="grid p-fluid"
                @submit.prevent="newFile"
            >
                <div class="col-12">
                    <h3>Give a name</h3>
                    <div class="p-inputgroup">
                        <InputText
                            v-model="newFileName"
                            type="text"
                            autofocus
                            placeholder="Name"
                            required
                        />
                    </div>
                </div>
            </form>
            <div
                v-if="newFileType === '*'"
                class="grid p-fluid"
                @drop="
                    async (event: DragEvent) =>
                        await newFiles(event.dataTransfer?.files)
                "
            >
                <div class="col-12">
                    <Button
                        class="p-button-secondary p-button-rounded theme-change"
                        icon="pi pi-upload"
                        :label="`Select files to add to to '${entity.name}'`"
                        @click="fileUploadElement?.click()"
                    />
                    <input
                        name="file"
                        type="file"
                        style="display: none"
                        ref="fileUploadElement"
                        @change="
                            async (event: HTMLInputEvent) =>
                                await newFiles(event.target?.files)
                        "
                        multiple
                    />
                </div>
            </div>
            <div v-if="newFileType === 'gdrive'" class="grid p-fluid">
                <GDrivePicker
                    @files-selected="newFiles"
                    @cancelled="showNewFileDialog = false"
                />
            </div>
            <template #footer>
                <Button
                    v-if="newFileType"
                    class="p-button"
                    icon="pi pi-chevron-left"
                    label="Back"
                    @click="newFileType = ''"
                />
                <Button
                    v-if="
                        [
                            'markdown',
                            'dmn',
                            'bpmn',
                            'form',
                            'domain',
                            'feature',
                        ].includes(newFileType)
                    "
                    class="p-button-primary"
                    icon="pi pi-check"
                    label="Create"
                    @click="newFile"
                />
            </template>
        </Dialog>
    </div>
</template>

<script lang="ts" setup>
import { useToast } from "primevue/usetoast";
import { computed, ref, toRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import baseForm from "@/assets/base.form?raw";
import GDrivePicker from "@/components/GDrivePicker.vue";
import type { Domain } from "@/composables/domain-manager";
import { useEditStore } from "@/composables/edit-manager";
import type { Process } from "@/composables/process-manager";
import type { Product } from "@/composables/product-manager";
import { useUniqueArtefactManager } from "@/composables/unique-artefact-manager";
import type { ArtefactMeta } from "@/lib/artefacts";
import { useArtefactsStore } from "@/lib/artefacts/user";
import Spinner from "@/shared/Spinner.vue";
import type { Task } from "@/stores/task-store";
import { HTMLInputEvent } from "@/types";
import { mime } from "@/utils";

type FileType = {
    name: string;
    value: string;
};

const props = withDefaults(
    defineProps<{
        entity: Domain | Product | Process | Task;
        label: string;
        header?: string;
        size: "large" | "small";
        navigateTo: string;
        showToastOnSave?: boolean;
        elementsInRow?: number;
        hiddenFileTypes?: string[];
        artefacts: ArtefactMeta[];
        severity?: string;
        textButton?: boolean;
    }>(),
    {
        header: "Add file",
        showToastOnSave: true,
        elementsInRow: 3,
        severity: "secondary",
        textButton: true,
    }
);

const fileTypes: FileType[] = [
    { name: "Text", value: "markdown" },
    { name: "Upload", value: "*" },
    { name: "Google Drive", value: "gdrive" },
    { name: "Form", value: "form" },
    { name: "DMN", value: "dmn" },
    { name: "BPMN", value: "bpmn" },
    { name: "Domain", value: "domain" },
    { name: "Gherkin", value: "feature" },
];

const toast = useToast();

const router = useRouter();
const route = useRoute();

const { isUploading, checkConflictsAndAddFile, checkConflictsAndAddFiles } =
    useUniqueArtefactManager(
        toRef(() => props.entity.user_artefact_group_id || ""),
        toRef(() => props.artefacts || [])
    );

const { updateArtefacts } = useArtefactsStore();

const showNewFileDialog = ref(false);
const newFileName = ref("");
const newFileType = ref("");

const fileUploadElement = ref<HTMLInputElement>();

const filteredFileTypes = computed(() => {
    return fileTypes.filter(
        (fileType: FileType) => !props.hiddenFileTypes?.includes(fileType.value)
    );
});

const navOnUploadCompleted = async (artefactIdentity: string, name: string) => {
    // Make sure the files are synced before navigating, otherwise we might
    // end up on a 404 page.
    await updateArtefacts();

    if (props.navigateTo) {
        await router.push({
            name: props.navigateTo,
            params: {
                ...route.params,
                artefactId: artefactIdentity,
            },
        });
    } else {
        await router.push({
            params: {
                ...route.params,
                filePath: name,
            },
        });
    }
    newFileName.value = "";
    newFileType.value = "";
};

const newFiles = async (files?: File[] | FileList) => {
    if (!files) return;
    await checkConflictsAndAddFiles(files, props.showToastOnSave);
    showNewFileDialog.value = false;
};

const { startEditing } = useEditStore();
const newFile = async () => {
    if (!newFileName.value) {
        toast.add({
            severity: "error",
            summary: "Please enter a file name",
        });
        return;
    }
    const filenameWithExtension = `${newFileName.value}.${newFileType.value}`;
    let content = "";

    // Add initial content for form files
    if (newFileType.value === "form") {
        // Parse and stringify to ensure valid JSON format
        content = JSON.stringify(JSON.parse(baseForm));
    }

    const file = new File([content], filenameWithExtension, {
        type: mime.getType(filenameWithExtension) ?? undefined,
        endings: "transparent",
    });

    await checkConflictsAndAddFile(
        filenameWithExtension,
        file,
        props.showToastOnSave,
        navOnUploadCompleted,
        () => {
            showNewFileDialog.value = true;
        }
    );

    showNewFileDialog.value = false;

    startEditing();
};
</script>
<style scoped lang="scss">
.conflict-dialog {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
</style>
